/**
 * LanguageSwitcher — small dropdown in the shared layout header.
 *
 * Reads the language list from `src/lib/languages.js`; only English is
 * fully populated today, other options fall back to English via i18next
 * `fallbackLng` until their resource JSON is added. Persists the choice
 * to localStorage so it survives reloads and route changes.
 */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { LANGUAGES } from "../lib/languages";
import { LS_KEY } from "../i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// Languages currently populated with a resource file. Extend as new
// `src/i18n/locales/<code>.json` files are added.
const POPULATED = new Set(["en"]);

export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const current = i18n.language || "en";

  useEffect(() => {
    try {
      window.localStorage?.setItem(LS_KEY, current);
    } catch { /* ignore */ }
  }, [current]);

  const onChange = (code) => i18n.changeLanguage(code);

  return (
    <div className="inline-flex items-center gap-2" data-testid="language-switcher">
      <Globe className="w-3.5 h-3.5 text-muted-foreground" aria-hidden />
      <Select value={current} onValueChange={onChange}>
        <SelectTrigger
          className={`h-8 ${compact ? "px-2 text-xs" : "px-3 text-sm"} min-w-[7rem] bg-transparent`}
          aria-label={t("common.languageSwitcher.label", "Language")}
          data-testid="language-switcher-trigger"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end" data-testid="language-switcher-menu">
          {LANGUAGES.map((l) => {
            const ready = POPULATED.has(l.code);
            return (
              <SelectItem
                key={l.code}
                value={l.code}
                data-testid={`language-option-${l.code}`}
              >
                <span className="flex items-center gap-2">
                  <span>{l.name}</span>
                  {!ready && (
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      · {t("common.languageSwitcher.comingSoon", "coming soon")}
                    </span>
                  )}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
